// TableGrid.jsx - Converted to Tailwind CSS
import { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faEdit,
} from "@fortawesome/free-solid-svg-icons"; 
import Loader from "components/ui/loader";
import Skeleton from "../../../components/ui/Skeleton";

// Natural stagger so cells don't all read the exact same width — premium feel.
const CELL_WIDTH_VARIANTS = ["94%", "68%", "82%", "58%", "76%"];

// Vertical column dividers — real 1px borders so they pixel-snap exactly like
// the row lines. The table uses border-separate so sticky cells keep them.
const COL_DIVIDER_RIGHT = "border-r border-border_soft";
const COL_DIVIDER_LEFT = "border-l border-border_soft";

// Smallest outer width (px) a column can be dragged to.
const MIN_COL_WIDTH = 60;
const SELECT_COL_KEY = "__select";
const ACTIONS_COL_KEY = "__actions";

// Skeleton Loader Component - mirrors the real table's column widths and
// sticky checkbox/actions layout 1:1 so there's no layout shift on data load.
const TableBodySkeleton = ({ columns, skeletonRows = 5, selectable = false, showActions = true }) => {
  // Each cell is its own fixed-width box (no flex gap) so the column dividers
  // line up between the header and every row. The last column stretches to
  // fill, mirroring the real table's w-full, so no divider floats mid-row.
  const cellClass = (colIndex) =>
    `flex items-center px-3.5 py-3 ${colIndex === columns.length - 1 ? "flex-1 min-w-0" : `shrink-0 ${COL_DIVIDER_RIGHT}`}`;
  const checkboxCellClass = `flex items-center shrink-0 px-3 py-3 ${COL_DIVIDER_RIGHT}`;
  const actionsCellClass = `flex items-center shrink-0 px-3 py-3 ${COL_DIVIDER_LEFT}`;

  return (
    <div className="flex flex-col h-[calc(100vh-375px)] bg-surface rounded-xl overflow-hidden border border-border">
      <div className="flex items-stretch border-b border-border_strong bg-background shrink-0">
        {selectable && (
          <div className={checkboxCellClass} style={{ width: "50px" }}>
            <Skeleton className="w-4 h-4" rounded="rounded" />
          </div>
        )}
        {columns.map((col, index) => (
          <div key={`header-${index}`} className={cellClass(index)} style={{ width: col.width || "100px" }}>
            <Skeleton className="h-3 w-3/4" />
          </div>
        ))}
        {showActions && (
          <div className={actionsCellClass} style={{ width: "120px" }}>
            <Skeleton className="h-3 w-16" />
          </div>
        )}
      </div>

      <div className="flex-1 overflow-hidden">
        {Array.from({ length: skeletonRows }).map((_, rowIndex) => (
          <div
            key={`row-${rowIndex}`}
            className={`flex items-stretch ${rowIndex === skeletonRows - 1 ? "" : "border-b border-border_soft"}`}
          >
            {selectable && (
              <div className={checkboxCellClass} style={{ width: "50px" }}>
                <Skeleton className="w-4 h-4" rounded="rounded" />
              </div>
            )}
            {columns.map((col, colIndex) => (
              <div key={`cell-${rowIndex}-${colIndex}`} className={cellClass(colIndex)} style={{ width: col.width || "100px" }}>
                <Skeleton
                  className="h-3"
                  style={{ width: CELL_WIDTH_VARIANTS[(rowIndex + colIndex) % CELL_WIDTH_VARIANTS.length] }}
                />
              </div>
            ))}
            {showActions && (
              <div className={`${actionsCellClass} gap-2`} style={{ width: "120px" }}>
                <Skeleton className="w-8 h-8" rounded="rounded" />
                <Skeleton className="w-14 h-7" rounded="rounded" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const TableGrid = ({
  data = [],
  loading = false,
  onSort,
  onRowClick,
  onTitleClick,
  onEdit,
  onSelectionChange,
  onExport,
  onDelete,
  currentPage = 1,
  pageSize = 10, //come this for skeletonRows (if loading == true)
  showActions = true,
  selectable = false,
  columns = [],
  sortConfig = { key: null, direction: "asc" },
  renderActions,
  resetSelectionTrigger,
  resizable = true,
}) => {
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [localData, setLocalData] = useState([]);
  // Excel-style column resizing. Empty until the first drag (table stays in auto
  // layout); after that it holds the outer px width of every column, keyed by
  // col.key plus SELECT_COL_KEY / ACTIONS_COL_KEY, and the table switches to
  // fixed layout driven by a <colgroup>.
  const [columnWidths, setColumnWidths] = useState({});
  const [resizingKey, setResizingKey] = useState(null);
  const resizeRef = useRef(null);
  const tableRef = useRef(null);
  // Auto-layout widths captured on the first drag — double-click resets to these.
  const initialWidthsRef = useRef({});

  // Restore body cursor/selection if unmounted mid-drag.
  useEffect(() => () => {
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);

  // Snapshot the current rendered width of every header cell, in column order.
  const measureColumnWidths = () => {
    const keys = [
      ...(selectable ? [SELECT_COL_KEY] : []),
      ...displayColumns.map((col) => col.key),
      ...(showActions ? [ACTIONS_COL_KEY] : []),
    ];
    const headerCells = tableRef.current?.querySelectorAll("thead th") || [];
    const widths = {};
    keys.forEach((key, i) => {
      if (headerCells[i]) widths[key] = headerCells[i].offsetWidth;
    });
    return widths;
  };

  const handleResizeStart = (e, key) => {
    e.preventDefault();
    e.stopPropagation();
    if (Object.keys(columnWidths).length === 0) {
      const measured = measureColumnWidths();
      initialWidthsRef.current = measured;
      setColumnWidths(measured);
    }
    resizeRef.current = { key, startX: e.clientX, startWidth: e.currentTarget.parentElement.offsetWidth };
    e.currentTarget.setPointerCapture(e.pointerId);
    setResizingKey(key);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const handleResizeMove = (e) => {
    const r = resizeRef.current;
    if (!r) return;
    const width = Math.max(MIN_COL_WIDTH, Math.round(r.startWidth + e.clientX - r.startX));
    setColumnWidths((prev) => (prev[r.key] === width ? prev : { ...prev, [r.key]: width }));
  };

  const handleResizeEnd = () => {
    if (!resizeRef.current) return;
    resizeRef.current = null;
    setResizingKey(null);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };

  // Double-click the handle to reset that column to its original width. Once
  // every column is back to its original, return the table to auto layout.
  const handleResizeReset = (key) => {
    setColumnWidths((prev) => {
      const initial = initialWidthsRef.current;
      if (initial[key] === undefined) return prev;
      const next = { ...prev, [key]: initial[key] };
      const allOriginal = Object.keys(next).every((k) => next[k] === initial[k]);
      return allOriginal ? {} : next;
    });
  };

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  useEffect(() => {
    if (resetSelectionTrigger === undefined) return;
    setSelectedRows(new Set());
    setSelectAll(false);
    onSelectionChange && onSelectionChange([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSelectionTrigger]);

  // Handle row selection
  const handleRowSelect = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
    setSelectAll(newSelected.size === localData.length);
    onSelectionChange && onSelectionChange(Array.from(newSelected));
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows(new Set());
      onSelectionChange && onSelectionChange([]);
    } else {
      const allIds = localData.map((item) => item.id || item.file_id);
      setSelectedRows(new Set(allIds));
      onSelectionChange && onSelectionChange(allIds);
    }
    setSelectAll(!selectAll);
  };

  // Handle sort
  const handleSort = (key) => {
    onSort && onSort(key);
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === "asc" ? faSortUp : faSortDown;
  };

  // Handle export
  const handleExport = () => {
    if (selectedRows.size > 0 && onExport) {
      onExport(Array.from(selectedRows));
    }
  };

  // Handle delete
  const handleDelete = () => {
    if (selectedRows.size > 0 && onDelete) {
      onDelete(Array.from(selectedRows));
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };
  // Get status text
  const getStatusText = (status) => {
    const statusText = {
      TO_DO: "To Do",
      READY_FOR_ESTIMATING: "Ready For Estimating",
      IN_PROGRESS: "In Progress",
      READY_FOR_REVIEW: "Ready For Review",
      IN_REVIEW: "In Review",
      DONE: "Done",
      CANCELED: "Canceled",
    };
    return statusText[status] || status;
  };

  // Default columns if not provided
  const defaultColumns = [
    {
      key: "file_id",
      label: "Job ID",
      type: "number",
      width: "100px",
      fixed: true,
      sortable: true,
    },
    {
      key: "title",
      label: "Job Name",
      type: "string",
      width: "250px",
      sortable: true,
    },
    {
      key: "client",
      label: "Client",
      type: "string",
      width: "150px",
      render: (row) =>
        `${row.client?.first_name || ""} ${row.client?.last_name || ""}`.trim() ||
        "-",
      sortable: true,
    },
    {
      key: "admin",
      label: "Assigned To",
      type: "string",
      width: "150px",
      render: (row) =>
        `${row.admin?.first_name || ""} ${row.admin?.last_name || ""}`.trim() ||
        "-",
      sortable: true,
    },
    {
      key: "priority",
      label: "Priority",
      type: "string",
      width: "120px",
      render: (row) => (
        <span
          className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide `}
        >
          {row.priority || "-"}
        </span>
      ),
      sortable: true,
    },
    {
      key: "status",
      label: "Status",
      type: "string",
      width: "130px",
      render: (row) => {
        const latestStatus =
          row.project_status_histories?.[
            row.project_status_histories.length - 1
          ]?.status;
        return (
          <span
            className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide `}
          >
            {getStatusText(latestStatus)}
          </span>
        );
      },
      sortable: true,
    },
    {
      key: "deadline",
      label: "Deadline",
      type: "date",
      width: "120px",
      render: (row) => formatDate(row.deadline),
      sortable: true,
    },
    {
      key: "created_date",
      label: "Created At",
      type: "date",
      width: "120px",
      render: (row) => formatDate(row.created_date),
      sortable: true,
    },
  ];

  const displayColumns = columns.length > 0 ? columns : defaultColumns;

  const isFixedLayout = Object.keys(columnWidths).length > 0;
  const totalFixedWidth = Object.values(columnWidths).reduce((sum, w) => sum + w, 0);
  // In fixed layout, cells clip at their own boundary and ellipsize text.
  const cellOverflow = isFixedLayout ? "overflow-hidden text-ellipsis" : "";

  // Show skeleton loader when loading
  if (loading) {
    return (
      <TableBodySkeleton
        columns={displayColumns}
        skeletonRows={pageSize}
        selectable={selectable}
        showActions={showActions}
      />
    );
  }
 

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="overflow-hidden flex flex-col h-[calc(100vh-375px)] bg-surface">
        {localData.length === 0 ? (
        <div className="flex items-center justify-center h-full"> 
        </div>
      ) : (
          <>
            <div className="flex-1 min-h-0 overflow-auto">
              <table
                ref={tableRef}
                className={`border-separate border-spacing-0 ${isFixedLayout ? "table-fixed" : "table-auto w-full min-w-max"}`}
                // Fill the container, but grow past it (horizontal scroll) once columns are widened.
                style={isFixedLayout ? { width: `max(100%, ${totalFixedWidth}px)` } : undefined}
              >
                {isFixedLayout && (
                  <colgroup>
                    {selectable && <col style={{ width: columnWidths[SELECT_COL_KEY] }} />}
                    {displayColumns.map((col, colIndex) => (
                      // Last data column stays unsized so it absorbs any spare width —
                      // no other column is stretched past the width the user set.
                      <col
                        key={col.key}
                        style={{ width: colIndex === displayColumns.length - 1 ? undefined : columnWidths[col.key] }}
                      />
                    ))}
                    {showActions && <col style={{ width: columnWidths[ACTIONS_COL_KEY] }} />}
                  </colgroup>
                )}
                <thead>
                  <tr>
                    {selectable && (
                      <th
                        className={`sticky left-0 top-0 z-30 bg-background text-text_primary font-semibold uppercase text-xs tracking-wide p-2.5 px-3 whitespace-nowrap border-b border-border_strong ${COL_DIVIDER_RIGHT}`}
                        style={{ width: "50px" }}
                      >
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          className="w-4 h-4 cursor-pointer accent-primary"
                        />
                      </th>
                    )}
                    {displayColumns.map((col, colIndex) => (
                      <th
                        key={col.key}
                        className={`sticky top-0 bg-background text-text_secondary
                          font-medium uppercase text-[10px] tracking-[0.05em]
                          py-3 px-3.5 whitespace-nowrap border-b border-border_strong ${cellOverflow}
                          ${colIndex < displayColumns.length - 1 ? COL_DIVIDER_RIGHT : ""}
                          ${col.fixed ? "left-0 z-30" : "z-20"}`}
                        style={{ width: isFixedLayout ? undefined : col.width || "auto" }}
                      >
                        <div className={`flex items-center gap-2 ${col.sortable !== false ? "cursor-pointer select-none" : "cursor-default"
                          }`}
                          onClick={() => col.sortable !== false && handleSort(col.key)}>
                          <span className="truncate min-w-0">{col.label}</span>
                          {col.sortable !== false && (
                            <FontAwesomeIcon
                              icon={getSortIcon(col.key)}
                              className={`text-[10px] shrink-0 ${sortConfig.key === col.key ? "text-primary opacity-100" : "opacity-40"
                                }`}
                            />
                          )}
                        </div>
                        {resizable && (
                          <div
                            role="separator"
                            aria-orientation="vertical"
                            aria-label={`Resize ${col.label} column`}
                            title="Drag to resize · double-click to reset"
                            className="absolute top-0 right-0 h-full w-2 cursor-col-resize touch-none z-10"
                            onPointerDown={(e) => handleResizeStart(e, col.key)}
                            onPointerMove={handleResizeMove}
                            onPointerUp={handleResizeEnd}
                            onPointerCancel={handleResizeEnd}
                            onDoubleClick={() => handleResizeReset(col.key)}
                          >
                            {resizingKey === col.key && (
                              <span className="absolute top-0 -right-px h-full w-0.5 bg-primary" />
                            )}
                          </div>
                        )}
                      </th>
                    ))}
                    {showActions && (
                      <th
                        className={`sticky right-0 top-0 z-30 bg-background text-text_primary font-semibold uppercase text-xs tracking-wide p-2.5 px-3 whitespace-nowrap border-b border-border_strong ${COL_DIVIDER_LEFT}`}
                        style={{ width: "120px" }}
                      >
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {localData.map((row, rowIndex) => {
                    const rowId = row.id || row.file_id;
                    // Last row skips its bottom line — the container border closes the table.
                    const rowBorder = rowIndex === localData.length - 1 ? "" : "border-b border-border_soft";
                    return (
                      <tr
                        key={rowId}
                        className={`transition-colors ${selectedRows.has(rowId) ? "bg-primary/[0.06]" : ""
                          } hover:bg-surface_alt`}
                      >
                        {selectable && (
                          <td
                            className={`sticky left-0 z-10 bg-surface p-2 px-3 ${rowBorder} whitespace-nowrap text-xs ${COL_DIVIDER_RIGHT}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={selectedRows.has(rowId)}
                              onChange={() => handleRowSelect(rowId)}
                              className="w-4 h-4 cursor-pointer accent-primary"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                        )}
                        {displayColumns.map((col, colIndex) => (
                          <td
                            key={col.key}
                            className={`bg-surface py-2 px-3.5 ${rowBorder} whitespace-nowrap
                              text-[12.5px] text-text_primary ${cellOverflow} ${col.fixed ? "sticky left-0 z-10" : ""}
                              ${colIndex < displayColumns.length - 1 ? COL_DIVIDER_RIGHT : ""}
                              ${col.key === "title" ? "cursor-pointer" : ""}`}
                            onClick={() => {
                              if (col.key === "title") {
                                onTitleClick && onTitleClick(row);
                              }
                            }}
                          >
                            {col.render ? col.render(row) : row[col.key] || "-"}
                          </td>
                        ))}
                        {showActions && (
                          <td
                            className={`sticky right-0 z-10 bg-surface p-2 px-3 ${rowBorder} whitespace-nowrap text-xs ${COL_DIVIDER_LEFT}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {renderActions ? (
                              renderActions(row)
                            ) : (
                              <div className="flex items-center gap-2 justify-center">
                                <button
                                  className="bg-transparent border border-border rounded w-8 h-8 flex items-center justify-center cursor-pointer text-primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onEdit && onEdit(row);
                                  }}
                                >
                                  <FontAwesomeIcon icon={faEdit} />
                                </button>

                                <button
                                  className="px-4 py-1 text-xs text-white bg-primary hover:bg-primary_hover transition-colors rounded"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onRowClick && onRowClick(row);
                                  }}
                                >
                                  View
                                </button>
                              </div>
                            )}
                          </td>
                        )}
                        {/* {showActions && (
                          <td
                            className="sticky right-0 z-0 bg-surface p-2 px-3 border-b border-border_soft whitespace-nowrap text-xs"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center gap-2 justify-center">
                              <button
                                className="bg-transparent border border-border rounded w-8 h-8 flex items-center justify-center cursor-pointer text-primary hover:bg-red-700 hover:text-white hover:border-red-700 transition-all duration-200"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEdit && onEdit(row);
                                }}
                                title="Edit Project"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button
                                className="inline-flex items-center gap-2 px-5 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-[#22179c] to-[#a84141] border-none rounded-lg cursor-pointer relative overflow-hidden max-w-fit whitespace-nowrap transition-transform duration-200 hover:scale-105 hover:shadow-lg group"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRowClick && onRowClick(row);
                                }}
                              >
                                View Details 
                                <span className="inline-block transition-transform group-hover:animate-bounceArrow">→</span>
                              </button>
                            </div>
                          </td>
                        )} */}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <style jsx>{`
        @keyframes bounceArrow {
          0%,
          100% {
            transform: translateX(0);
          }
          50% {
            transform: translateX(8px);
          }
        }
        .group:hover .animate-bounceArrow {
          animation: bounceArrow 0.5s infinite alternate;
        }
        /* Custom scrollbar */
        .overflow-auto::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .overflow-auto::-webkit-scrollbar-track {
          background: transparent;
          border-radius: 4px;
        }
        .overflow-auto::-webkit-scrollbar-thumb {
          background: var(--color-border-strong);
          border-radius: 4px;
        }
        .overflow-auto::-webkit-scrollbar-thumb:hover {
          background: var(--color-text-muted);
        }
      `}</style>
    </div>
  );
};

export default TableGrid;
